// СТЕНД ГАРДА ПЕРЕЛИВА ПАСПОРТА — `?view=docfit` (тикет паспорта s969, `3f019875cbb881ef9bfbe5934a58ad90`).
//
// Гоняет НАСТОЯЩУЮ дорогу документа шага 7 (B9-6): `documentHtml` → `paginateWindow` (§II по замеру)
// → `fitDocument` в самом окне (§IV лестница CD, §VII, подписи) — в iframe шириной в лист (1240,
// масштаб 1), по очереди на каждом случае N × M, и публикует итог JSON-ом в `docfit-results`.
// Его читает `tests/e2e/passport-docfit.spec.js`. Стенд в бандле, потому что e2e в CI идёт по
// собранному preview, — так же, как `?view=passport`.
//
// Синтетика — ХУДШИЙ легальный контент, а не типичный: имена этапов на капе `STAGE_NAME_MAX`,
// каждая группа кнопок на своём `max` самыми длинными вариантами, цитаты длиннее капа §IV
// (рендер режет их сам, `capQuote`). Носители M назначаются последним M этапам прямо в паспорте:
// здесь проверяется вёрстка при M карточках, а не движок, который их выбирает.

import { useEffect, useRef, useState } from 'react';
import { buildPassport } from './passportData.js';
import { BUTTON_GROUPS, STAGE_NAME_MAX } from '../contracts.js';
import { documentHtml, paginateWindow } from '../wizard/steps/s7Document.js';

const LAYERS = ['Vitality', 'Security', 'Growth', 'Authorship', 'Wanting', 'Yourself', 'Lasting'];
const QUOTE = 'knew it the whole time and kept telling myself next year, next year, once the kids are older, once the money is steadier, once someone asks — and nobody asks, so the year after that it was the same sentence, word for word, only quieter, and I stopped hearing it';

const longest = (key) => [...BUTTON_GROUPS[key].options].sort((a, b) => b.length - a.length).slice(0, BUTTON_GROUPS[key].max);

/** Синтетический паспорт: N этапов, M носителей (последние M), контент на капах. */
export function stressPassport(N, M, { nameLen = STAGE_NAME_MAX, quoteLen = QUOTE.length } = {}) {
  const stages = Array.from({ length: N }, (_, i) => {
    const base = `stage ${i + 1} — the long years of saying yes to everything that was asked of me`;
    return {
      name: base.padEnd(nameLen, ' and more').slice(0, nameLen),
      startY: 1990 + i * 2, endY: 1990 + i * 2 + 2, current: i === N - 1, openStart: false,
    };
  });
  stages[N - 1].endY = null;
  const sliders = stages.map((_, i) => Object.fromEntries(LAYERS.map((L, j) => [L, ((i + j) % 7) - 3])));
  const text = stages.map(() => ({ T1: QUOTE, T2: QUOTE, T3: QUOTE }));
  const buttons = stages.map(() => ({ BG1: longest('BG1'), BG2: longest('BG2'), BG3: longest('BG3'), BG4: longest('BG4') }));
  const carriers = Array.from({ length: M }, (_, k) => N - M + k);
  const system2 = { sectionIV: { evidence: carriers.map((stage) => ({ stage, quote: QUOTE.slice(0, quoteLen) })) } };
  const p = buildPassport({ wizard: { stages, sliders, text, buttons } }, system2);
  return { ...p, carrierStages: { ...p.carrierStages, indices: carriers } };
}

/** Все случаи гарда: N = 2…15, M = 1…min(6, N). */
export const CASES = Array.from({ length: 14 }, (_, k) => k + 2)
  .flatMap((N) => Array.from({ length: Math.min(6, N) }, (_, m) => [N, m + 1]));

function parseCases() {
  const q = new URLSearchParams(window.location.search).get('cases');
  if (!q || q === 'all') return CASES;
  return q.split(',').map((c) => c.split('x').map(Number));
}

/** Один случай в iframe: вернуть план §II, перелив, режим §IV. */
async function runCase(frame, N, M) {
  let passport;
  const q = new URLSearchParams(window.location.search);
  const len = { ...(q.get('name') ? { nameLen: +q.get('name') } : {}), ...(q.get('quote') ? { quoteLen: +q.get('quote') } : {}) };
  try {
    passport = stressPassport(N, M, len);
  } catch (e) {
    return { N, M, error: String(e.message || e).slice(0, 160) };
  }
  const opts = { generatedAt: '2026-10-05' };
  frame.srcdoc = documentHtml(passport, opts);
  await new Promise((r) => frame.addEventListener('load', r, { once: true }));
  const win = frame.contentWindow;
  const plan = await paginateWindow(win, passport, opts);
  const doc = await new Promise((resolve) => {
    (function poll() {
      const d = win.document;
      if (d.documentElement.getAttribute('data-fit') === 'done') resolve(d);
      else setTimeout(poll, 30);
    })();
  });
  const sheetsII = [...doc.querySelectorAll('section.stages-page')].map((s) => s.querySelectorAll('.st-card').length);
  const overflow = [...doc.querySelectorAll('[data-overflow-px]')]
    .filter((e) => e.getAttribute('data-overflow-px'))
    .map((e) => `${e.getAttribute('data-screen-label') || e.className.split(' ')[0]}: ${e.getAttribute('data-overflow-px')}px`);
  const iv = doc.querySelector('.rd-cross')?.closest('.artboard');
  return {
    N, M, plan, sheetsII, overflow,
    evCards: doc.querySelectorAll('.r4-ev').length,
    ivFit: iv ? iv.dataset.fit || '' : null,
  };
}

export default function DocFitStand() {
  const frameRef = useRef(null);
  const [results, setResults] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const out = [];
      for (const [N, M] of parseCases()) {
        if (!alive) return;
        out.push(await runCase(frameRef.current, N, M));
      }
      if (alive) setResults(out);
    })();
    return () => { alive = false; };
  }, []);
  return (
    <div style={{ background: '#0a0b0d', color: '#ccc', padding: 16, fontFamily: 'monospace' }}>
      <pre data-testid="docfit-results" data-state={results ? 'done' : 'running'}>
        {results ? JSON.stringify(results) : 'running…'}
      </pre>
      <iframe ref={frameRef} title="docfit" style={{ width: 1240, height: 1754, border: 0, background: '#000' }} />
    </div>
  );
}
